import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Form, Input, Button, Card, Typography, Space, Row, Col, message, Upload, Tooltip, Tag, Divider, Modal, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined, CheckOutlined, CloseOutlined, ProjectOutlined, UserAddOutlined } from '@ant-design/icons';
import projectService from '../services/projectService';
import { Project, ProjectCreate, ProjectRole, ProjectUserInvite, hasAdminPrivileges, ProjectType } from '../types/project';
import { RcFile } from 'antd/es/upload';
import { API_CONFIG } from '../config';
import EditProjectForm from '../components/projects/EditProjectForm';
import ProjectUsers from '../components/projects/ProjectUsers';
import { getAuthToken } from '../utils/auth';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const ProjectsPage: React.FC = () => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [form] = Form.useForm<ProjectCreate>();
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [selectedProjectForInvite, setSelectedProjectForInvite] = useState<Project | null>(null);
  const [inviteForm] = Form.useForm<ProjectUserInvite>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    return () => {
      if (selectedFile) {
        URL.revokeObjectURL(URL.createObjectURL(selectedFile));
      }
    };
  }, [selectedFile]);

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await projectService.getUserProjects();
      setProjects(data);
      
      // Сохраняем ID текущего выбранного проекта
      const currentSelectedProjectId = selectedProject?.id;
      
      // Если есть выбранный проект, обновляем его данные
      if (currentSelectedProjectId) {
        const updatedSelectedProject = data.find(p => p.id === currentSelectedProjectId);
        if (updatedSelectedProject) {
          setSelectedProject(updatedSelectedProject);
        }
      } else if (data.length > 0) {
        // Если нет выбранного проекта, выбираем первый
        setSelectedProject(data[0]);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError(t('projects.fetchError'));
      message.error(t('projects.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [t, selectedProject?.id]);

  const fetchProject = async (projectId: number) => {
    try {
      const project = await projectService.getProject(projectId);
      setSelectedProject(project);
    } catch (error) {
      console.error('Error fetching project:', error);
      message.error(t('projects.fetchError'));
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreateProject = async (values: ProjectCreate) => {
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('name', values.name);
      formData.append('description', values.description || '');
      formData.append('domain', values.domain || '');
      formData.append('prefix', values.prefix || '');
      formData.append('project_type', values.project_type || 'default');
      
      if (selectedFile) {
        formData.append('file', selectedFile);
      }
      
      await projectService.createProject(formData as unknown as ProjectCreate);
      message.success(t('projects.createdSuccess'));
      setIsCreating(false);
      form.resetFields();
      setSelectedFile(null);
      fetchProjects();
    } catch (error: any) {
      console.error('Error creating project:', error);
      message.error(error.response?.data?.detail || t('projects.createError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (project: Project) => {
    Modal.confirm({
      title: t('projects.confirmDelete'),
      content: t('projects.confirmDeleteMessage', { name: project.name }),
      okText: t('common.yes'),
      okType: 'danger',
      cancelText: t('common.no'),
      centered: true,
      width: 500,
      footer: (_, { OkBtn, CancelBtn }) => (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <CancelBtn />
          <OkBtn />
        </div>
      ),
      onOk: async () => {
        try {
          await projectService.deleteProject(project.id);
          message.success(t('projects.deleteSuccess'));
          fetchProjects();
          setSelectedProject(null);
        } catch (error) {
          console.error('Error deleting project:', error);
          message.error(t('projects.errors.deleteFailed'));
        }
      }
    });
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
  };

  const handleUpdateProject = async () => {
    try {
      if (editingProject) {
        const updatedProject = await projectService.getProject(editingProject.id);
        setProjects(projects.map(p => p.id === editingProject.id ? updatedProject : p));
        if (selectedProject?.id === editingProject.id) {
          setSelectedProject(updatedProject);
        }
        message.success(t('projects.updatedSuccess'));
        setEditingProject(null);
      }
    } catch (error) {
      message.error(t('projects.updateError'));
    }
  };

  const handleUpload = async (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error(t('projects.uploadImageOnly'));
      return false;
    }
    setSelectedFile(file);
    form.setFieldValue('image_url', file.name);
    return false;
  };

  const handleUploadChange = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
    }
  };

  const handleInviteUser = async (values: ProjectUserInvite) => {
    if (!selectedProjectForInvite) return;
    try {
      setLoading(true);
      await projectService.inviteUser(selectedProjectForInvite.id, values);
      message.success(t('project.users.inviteSuccess'));
      setInviteModalVisible(false);
      inviteForm.resetFields();
      await fetchProjects();
    } catch (error: any) {
      console.error('Error inviting user:', error);
      
      // Обработка различных типов ошибок
      if (error.response) {
        switch (error.response.status) {
          case 404:
            if (error.response.data?.detail === "User not found") {
              message.error(t('project.users.errors.userNotFound'));
            } else if (error.response.data?.detail === "Project not found") {
              message.error(t('project.users.errors.projectNotFound'));
            }
            break;
          case 403:
            message.error(t('project.users.errors.noPermission'));
            break;
          case 400:
            if (error.response.data?.detail === "User is already in project") {
              message.warning(t('project.users.errors.alreadyInProject'));
            }
            break;
          default:
            message.error(t('project.users.errors.unknownError'));
        }
      } else if (error.request) {
        // Ошибка сети
        message.error(t('project.users.errors.networkError'));
      } else {
        // Другие ошибки
        message.error(t('project.users.errors.generalError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: ProjectRole) => {
    try {
      setLoading(true);
      await projectService.updateUserRole(selectedProject?.id!, userId, newRole);
      message.success(t('project.users.roleUpdated'));
      await fetchProjects();
      if (selectedProject) {
        fetchProject(selectedProject.id);
      }
    } catch (error: any) {
      console.error('Error updating user role:', error);
      
      if (error.response) {
        switch (error.response.status) {
          case 403:
            message.error(t('project.users.errors.noPermission'));
            break;
          case 404:
            message.error(t('project.users.errors.userNotFound'));
            break;
          default:
            message.error(t('project.users.errors.roleUpdateFailed'));
        }
      } else {
        message.error(t('project.users.errors.generalError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveUser = async (userId: number) => {
    try {
      setLoading(true);
      await projectService.removeUser(selectedProject?.id!, userId);
      message.success(t('project.users.userRemoved'));
      await fetchProjects();
      if (selectedProject) {
        fetchProject(selectedProject.id);
      }
    } catch (error: any) {
      console.error('Error removing user:', error);
      
      if (error.response) {
        switch (error.response.status) {
          case 403:
            message.error(t('project.users.errors.noPermission'));
            break;
          case 404:
            message.error(t('project.users.errors.userNotFound'));
            break;
          default:
            message.error(t('project.users.errors.removeFailed'));
        }
      } else {
        message.error(t('project.users.errors.generalError'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <Title level={2}>{t('projects.title')}</Title>
        </Col>
        <Col>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsCreating(true)}
          >
            {t('projects.createProject')}
          </Button>
        </Col>
      </Row>

      <Row gutter={24}>
        <Col span={12}>
      {isCreating && (
        <Card style={{ marginBottom: '24px' }}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleCreateProject}
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label={t('projects.name')}
                  rules={[{ required: true, message: t('projects.nameRequired') }]}
                >
                  <Input placeholder={t('projects.namePlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="prefix"
                  label={t('projects.prefix')}
                  tooltip={t('projects.prefixTooltip')}
                >
                  <Input placeholder={t('projects.prefixPlaceholder')} maxLength={10} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="domain"
                  label={t('projects.domain')}
                >
                  <Input placeholder={t('projects.domainPlaceholder')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="project_type"
                  label={t('projects.type')}
                  rules={[{ required: true, message: t('projects.typeRequired') }]}
                >
                  <Select>
                    <Select.Option value={ProjectType.WEB}>{t('projects.types.web')}</Select.Option>
                    <Select.Option value={ProjectType.ANDROID}>{t('projects.types.android')}</Select.Option>
                    <Select.Option value={ProjectType.IOS}>{t('projects.types.ios')}</Select.Option>
                    <Select.Option value={ProjectType.MACOS}>{t('projects.types.macos')}</Select.Option>
                    <Select.Option value={ProjectType.WINDOWS}>{t('projects.types.windows')}</Select.Option>
                    <Select.Option value={ProjectType.LINUX}>{t('projects.types.linux')}</Select.Option>
                    <Select.Option value={ProjectType.DESKTOP}>{t('projects.types.desktop')}</Select.Option>
                    <Select.Option value={ProjectType.MOBILE}>{t('projects.types.mobile')}</Select.Option>
                    <Select.Option value={ProjectType.BACKEND}>{t('projects.types.backend')}</Select.Option>
                    <Select.Option value={ProjectType.API}>{t('projects.types.api')}</Select.Option>
                    <Select.Option value={ProjectType.EMBEDDED}>{t('projects.types.embedded')}</Select.Option>
                    <Select.Option value={ProjectType.GAME}>{t('projects.types.game')}</Select.Option>
                    <Select.Option value={ProjectType.OTHER}>{t('projects.types.other')}</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="image_url"
                  label={t('projects.image')}
                >
                  <Upload
                    name="file"
                    listType="picture-card"
                    showUploadList={false}
                    beforeUpload={handleUpload}
                    onChange={handleUploadChange}
                    style={{ width: '100%' }}
                    fileList={[]}
                  >
                    {selectedFile ? (
                      <img
                        src={URL.createObjectURL(selectedFile)}
                        alt="preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <div>
                        <UploadOutlined />
                        <div style={{ marginTop: 8 }}>{t('projects.uploadImage')}</div>
                      </div>
                    )}
                  </Upload>
                </Form.Item>
              </Col>
            </Row>
            <Form.Item
              name="description"
              label={t('projects.description')}
            >
              <TextArea rows={4} placeholder={t('projects.descriptionPlaceholder')} />
            </Form.Item>
            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit">
                  {t('projects.create')}
                </Button>
                <Button onClick={() => {
                  setIsCreating(false);
                  form.resetFields();
                }}>
                  {t('projects.cancel')}
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>
      )}

      <Row gutter={[24, 24]}>
        {projects.map((project) => (
              <Col span={24} key={project.id}>
            <Card
              hoverable
                  onClick={() => setSelectedProject(project)}
                  style={{
                    cursor: 'pointer',
                    border: selectedProject?.id === project.id ? '2px solid #1890ff' : undefined
                  }}
              cover={
                project.image_url ? (
                  <img
                    alt={project.name}
                    src={project.image_url}
                    style={{ height: 160, objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{ height: 160, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProjectOutlined style={{ fontSize: 48, color: '#bfbfbf' }} />
                  </div>
                )
              }
              actions={[
                    hasAdminPrivileges(project.role as ProjectRole) && (
                      <Tooltip title={t('project.users.inviteUser')}>
                        <UserAddOutlined key="invite" onClick={(e) => {
                          e.stopPropagation();
                          setSelectedProjectForInvite(project);
                          setInviteModalVisible(true);
                        }} />
                      </Tooltip>
                    ),
                <Tooltip title={t('projects.edit')}>
                      <EditOutlined key="edit" onClick={(e) => {
                        e.stopPropagation();
                        handleEditProject(project);
                      }} />
                </Tooltip>,
                <Tooltip title={t('projects.delete')}>
                      <DeleteOutlined key="delete" onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project);
                      }} />
                    </Tooltip>
                  ].filter(Boolean)}
            >
              <Card.Meta
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{project.name}</span>
                    <Tag color="blue">{t(`projects.types.${(project.project_type || 'default').toLowerCase()}`)}</Tag>
                  </div>
                }
                description={
                  <div>
                    <div style={{ marginBottom: 8 }}>{project.description}</div>
                    <div>
                      <Text type="secondary">{project.domain}</Text>
                    </div>
                  </div>
                }
              />
            </Card>
          </Col>
        ))}
      </Row>
        </Col>

        <Col span={12}>
          {selectedProject ? (
            <Card
              title={t('projects.userManagement.title')}
              style={{ height: '100%', overflow: 'hidden' }}
            >
              <div style={{ height: 'calc(100vh - 300px)', overflow: 'auto' }}>
                <ProjectUsers 
                  projectId={selectedProject.id} 
                  currentUserRole={selectedProject.userRole}
                  onUpdate={() => {
                    fetchProjects();
                    if (selectedProject) {
                      fetchProject(selectedProject.id);
                    }
                  }}
                  projectName={selectedProject.name}
                />
              </div>
            </Card>
          ) : (
            <Card style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Text type="secondary">{t('projects.select.noProject')}</Text>
            </Card>
          )}
        </Col>
      </Row>

      <Modal
        title={t('projects.editProject')}
        open={!!editingProject}
        onCancel={() => setEditingProject(null)}
        footer={null}
        width={800}
        destroyOnHidden
      >
        {editingProject && (
          <EditProjectForm
            project={editingProject}
            onSuccess={handleUpdateProject}
            onCancel={() => setEditingProject(null)}
          />
        )}
      </Modal>

      <Modal
        title={t('project.users.inviteUser')}
        open={inviteModalVisible}
        onCancel={() => {
          setInviteModalVisible(false);
          inviteForm.resetFields();
        }}
        footer={null}
        width={800}
        destroyOnHidden
      >
        <Form
          form={inviteForm}
          layout="vertical"
          onFinish={handleInviteUser}
        >
          <Form.Item
            name="email"
            label={t('project.users.email')}
            rules={[
              { required: true, message: t('project.users.emailRequired') },
              { type: 'email', message: t('project.users.invalidEmail') }
            ]}
          >
            <Input placeholder={t('project.users.emailPlaceholder')} />
          </Form.Item>

          <Form.Item
            name="role"
            label={t('project.users.role')}
            rules={[{ required: true, message: t('project.users.roleRequired') }]}
          >
            <Select placeholder={t('project.users.rolePlaceholder')}>
              <Select.Option value={ProjectRole.ADMIN}>{t('project.roles.admin')}</Select.Option>
              <Select.Option value={ProjectRole.MEMBER}>{t('project.roles.member')}</Select.Option>
              <Select.Option value={ProjectRole.VIEWER}>{t('project.roles.viewer')}</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              {t('project.users.sendInvite')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ProjectsPage; 